import React, { FC, useContext, useEffect } from "react";
import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { FDADesignations } from "../../typescript/enums/data";
import { ComplexFormTypeDescription, OtherTag, RxStrategyPipelines } from "../../typescript/interfaces/data";
import Commentary from "./Commentary";
import PanelSection from "./PanelSection";
import { VisibilityContext } from "../../context";
import { filter } from "../../functions";
import { PERMISSIONS } from "../../constants";
import StyledTooltip from "../Common/StyledTooltip";
import config from "../../config";

const useStyles = makeStyles({
  panelContainer: {
    minHeight: "2rem",
    paddingTop: "1rem",
    paddingBottom: "1rem",
  },
  commentary: {
    textAlign: "left",
  },
});

type DetailPanelProps = {
  tags: (OtherTag | FDADesignations)[];
  notesAndCommentary: RxStrategyPipelines[];
  complexForms: ComplexFormTypeDescription[];
  likelyComplexForms: ComplexFormTypeDescription[];
  rowId: number;
  handleUnmount: (rowId: number) => void;
};

const DetailPanel: FC<DetailPanelProps> = ({
  tags,
  complexForms,
  likelyComplexForms,
  notesAndCommentary,
  rowId,
  handleUnmount,
}) => {
  const classes = useStyles();
  const hasRxStrategyPipelines = notesAndCommentary.length > 0;
  const links = notesAndCommentary.map((pipeline) => pipeline.rxStrategy);

  const [visibilityContext, setVisibilityContext] = useContext(VisibilityContext);

  useEffect(() => {
    if (typeof rowId === "number") {
      setVisibilityContext({
        ...visibilityContext,
        detailPanelsOpen: filter.newArray(visibilityContext.detailPanelsOpen, rowId),
        rowsExpanded: filter.newArray(visibilityContext.rowsExpanded, rowId),
      });
    }

    return () => handleUnmount(rowId);
  }, []);

  const showComplexTag =
    complexForms.length > 0 || (config.featureToggles.likelyComplexFormReleased && likelyComplexForms.length > 0);
  return (
    <Grid container className={classes.panelContainer}>
      <Grid item xs={11}>
        {tags.length > 0 && (
          <Grid container spacing={2}>
            <PanelSection header="Tags:" items={tags} />
          </Grid>
        )}
        {showComplexTag && (
          <Grid container spacing={2}>
            <PanelSection
              header="Complexity:"
              items={complexForms
                .map((x) => (
                  <StyledTooltip key={x.complexForm} title={x.description}>
                    <span>{x.name}</span>
                  </StyledTooltip>
                ))
                .concat(
                  (config.featureToggles.likelyComplexFormReleased ? likelyComplexForms : []).map((x) => (
                    <StyledTooltip key={x.complexForm} title={x.description}>
                      <span>{x.name} *</span>
                    </StyledTooltip>
                  ))
                )}
            />
          </Grid>
        )}
        {hasRxStrategyPipelines && (
          <>
            {notesAndCommentary.map(({ rxStrategy, comment }) => {
              if (comment !== null) {
                return (
                  <div key={rxStrategy.id}>
                    {rxStrategy && (
                      <Grid container spacing={2}>
                        <PanelSection
                          header="Clinical Development Notes:"
                          items={[rxStrategy.clinicalDevelopmentNotes]}
                        />
                      </Grid>
                    )}
                    {comment && (
                      <Grid item xs={12} className={classes.commentary}>
                        <Commentary comment={comment} />
                      </Grid>
                    )}
                  </div>
                );
              }
            })}
          </>
        )}
        {hasRxStrategyPipelines && (
          <Grid container spacing={2}>
            <PanelSection
              header="Additional Payer and Provider Insights:"
              links={links}
              permissions={PERMISSIONS.PPI}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default DetailPanel;
